import { CreateDraft } from './types';

export type SnapGranularity = 'day' | 'hour' | '10min';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 表示スパン（ミリ秒）からスナップ粒度を決定する。
 * - 年/月表示（>14日）→ 1日単位
 * - 週表示（>3日）→ 1時間単位
 * - 日表示（≤3日）→ 10分単位
 */
export const getSnapGranularity = (spanMs: number): SnapGranularity => {
  const spanDays = spanMs / DAY_MS;
  if (spanDays > 14) {
    return 'day';
  }
  if (spanDays > 3) {
    return 'hour';
  }
  return '10min';
};

export const snapToGranularity = (date: Date, granularity: SnapGranularity): string => {
  switch (granularity) {
    case 'day': {
      const HALF_DAY_MS = 12 * 60 * 60 * 1000;
      const shifted = new Date(date.getTime() + HALF_DAY_MS);
      return new Date(
        shifted.getFullYear(),
        shifted.getMonth(),
        shifted.getDate(),
      ).toISOString();
    }
    case 'hour':
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
      ).toISOString();
    case '10min': {
      const minutes = Math.round(date.getMinutes() / 10) * 10;
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        minutes,
      ).toISOString();
    }
  }
};

export const toStartOfDayIso = (date: Date): string => {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return dayStart.toISOString();
};

export const toNearestMinuteIso = (date: Date): string => {
  const rounded = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
  );
  return rounded.toISOString();
};

export const isSameUtcDay = (startDate: string, endDate: string): boolean => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return (
    start.getUTCFullYear() === end.getUTCFullYear()
    && start.getUTCMonth() === end.getUTCMonth()
    && start.getUTCDate() === end.getUTCDate()
  );
};

export const isSameUtcMinute = (dateA: string, dateB: string): boolean => {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return (
    a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate()
    && a.getUTCHours() === b.getUTCHours()
    && a.getUTCMinutes() === b.getUTCMinutes()
  );
};

export const normalizeDurationRange = (firstDate: string, secondDate: string): CreateDraft => {
  if (new Date(firstDate).getTime() <= new Date(secondDate).getTime()) {
    return { startDate: firstDate, endDate: secondDate };
  }
  return { startDate: secondDate, endDate: firstDate };
};

export const normalizeEditableRange = (firstDate: string, secondDate: string): CreateDraft => {
  const normalized = normalizeDurationRange(firstDate, secondDate);
  if (!normalized.endDate) {
    return { startDate: normalized.startDate };
  }
  return normalized;
};

/**
 * 同一分の期間イベントをリサイズして同じ分に留まった場合、ポイントイベントに変換する。
 * それ以外の期間イベントを同一分にリサイズした場合は期間イベントのまま維持する。
 */
export const resolveResizedRange = (
  initialRange: CreateDraft,
  resizedRange: CreateDraft,
): CreateDraft => {
  const wasSameMinuteDuration = initialRange.endDate
    && isSameUtcMinute(initialRange.startDate, initialRange.endDate);
  const isSameMinute = resizedRange.endDate
    && isSameUtcMinute(resizedRange.startDate, resizedRange.endDate);
  if (wasSameMinuteDuration && isSameMinute) {
    return { startDate: resizedRange.startDate };
  }
  return resizedRange;
};

export const shiftUtcDateByDays = (dateValue: string, days: number): string => {
  const date = new Date(dateValue);
  const shifted = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + days,
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  ));
  return shifted.toISOString();
};

export const shiftDateByMs = (dateValue: string, deltaMs: number, granularity?: SnapGranularity): string => {
  const date = new Date(dateValue);
  const shifted = new Date(date.getTime() + deltaMs);
  if (granularity) {
    return snapToGranularity(shifted, granularity);
  }
  return toNearestMinuteIso(shifted);
};
